// Storage of a day's contract inside its daily note - see
// doc/contract-motivation-concept.org §4.5.
//
// The contract is written as real Org tasks: a container headline for the day,
// with each point as its own TODO/DONE subheading - so the contract is a
// first-class Org artifact any other tool (agenda, mobile client) understands
// without knowing anything about organice.
//
// Each of those headlines also states which Kontrakt it belongs to, in the
// annotation format of the entropie-dashboard (ADR-0015), so the day's promise
// shows up as a strand there rather than falling into a collector:
//
//   * TODO Tagesvertrag vom <2026-08-02> :tagesvertrag:
//   :PROPERTIES:
//   :KONTRAKT: tagesvertrag
//   :END:
//   ** TODO After standup: write the release notes
//   DEADLINE: <2026-08-02>
//   :PROPERTIES:
//   :KONTRAKT: tagesvertrag
//   :END:
//   ** DONE 30 min: empty inbox
//   DEADLINE: <2026-08-02> CLOSED: [2026-08-02 Sun 11:03]
//   :PROPERTIES:
//   :KONTRAKT: tagesvertrag
//   :END:
//
// The container headline's own keyword is derived, not chosen: TODO until
// every point is DONE, then DONE itself - and back to TODO the moment a point
// reopens, since the whole node is regenerated on every save rather than
// patched in place.
//
// Read and written by line-level text surgery rather than a parseOrg/exportOrg
// round trip: the daily note is the user's file, and pushing it through the
// exporter could reformat content the contract never touched. Surgery replaces
// the container headline's *entire* subtree (the headline plus every point
// under it) and leaves everything outside that subtree untouched - which also
// means nothing should be added by hand inside this node, since the next save
// regenerates it wholesale.

import { dayKey, isKept, normalizeContract } from './contract';
import { KONTRAKT, KONTRAKT_ID, KONTRAKT_MIT, KONTRAKT_ZUSAGE } from './kontrakt';

export const CONTRACT_TAG = 'tagesvertrag';

/**
 * The Kontrakt every day's contract belongs to (ADR-0015 of the
 * entropie-dashboard). One long-lived strand, not one per day: a Tagesvertrag
 * is a single continuing promise to oneself with a daily cadence, and a strand
 * per day would add 365 strands and 365 Kontexte a year to a view built for
 * twenty. The declaring headline lives once in the GTD file; see
 * ContractDashboard.
 */
export const CONTRACT_KONTRAKT_ID = 'tagesvertrag';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (number) => `${number}`.padStart(2, '0');

/** An inactive org timestamp, minute precision: [2026-08-02 Sun 09:15]. */
export const formatOrgTimestamp = (millis) => {
  const date = new Date(millis);

  return (
    `[${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${WEEKDAYS[date.getDay()]} ${pad(date.getHours())}:${pad(date.getMinutes())}]`
  );
};

/** An active org date, no weekday, no time - valid for a DEADLINE line: <2026-08-02>. */
export const formatOrgDate = (date) => `<${dayKey(date)}>`;

/**
 * A timestamp (either format, with or without a weekday or a time) back to
 * millis, as local time. Null for anything that holds no date at all.
 */
export const parseOrgTimestamp = (value) => {
  const match = /(\d{4})-(\d{2})-(\d{2})(?:[^\d]+(\d{1,2}):(\d{2}))?/.exec(`${value || ''}`);
  if (!match) return null;

  return new Date(
    +match[1],
    +match[2] - 1,
    +match[3],
    +(match[4] || 0),
    +(match[5] || 0)
  ).getTime();
};

const headlineLevel = (line) => {
  const match = /^(\*+)\s/.exec(line);
  return match ? match[1].length : null;
};

const headlineTags = (line) => {
  const match = /:((?:[^\s:]+:)+)\s*$/.exec(line);
  return match ? match[1].split(':').filter(Boolean) : [];
};

// Exactly one star and the contract tag: the user asked for the main node, and
// a deeper or differently-tagged headline of the same title is somebody's own,
// not ours to rewrite.
const isContractHeadline = (line) =>
  headlineLevel(line) === 1 && headlineTags(line).some((tag) => tag.toLowerCase() === CONTRACT_TAG);

/** Index one past the last line belonging to the subtree that starts at `index`. */
const endOfSubtreeIndex = (lines, index) => {
  let end = index + 1;
  while (end < lines.length) {
    const level = headlineLevel(lines[end]);
    if (level !== null && level <= 1) break;
    end += 1;
  }

  // A blank-line separator right before the next headline (or EOF) is spacing
  // between subtrees, not part of this one - excluding it from what gets
  // replaced means resaving does not eat that spacing line by line.
  while (end > index + 1 && lines[end - 1] === '') end -= 1;

  return end;
};

// Membership is stated on the container *and* on every point, because
// `:KONTRAKT:` is the one annotation that does not inherit - only a
// `:KONTRAKT_ID:` declaration passes down a subtree. Stating it once on the
// container would leave every point in the collector strand while the day's
// headline sat in `tagesvertrag`. This is exactly how archiv-2026.org spreads
// a journal across strands: one drawer per entry.
//
// The drawer belongs directly under the headline, after the planning line -
// further down both parsers read it as prose (README-ORGMODE §3).
const kontraktDrawer = () => [':PROPERTIES:', `:${KONTRAKT}: ${CONTRACT_KONTRAKT_ID}`, ':END:'];

const pointLines = (point, today) => {
  const planning = [`DEADLINE: ${formatOrgDate(today)}`];
  if (point.done && point.doneAt) planning.push(`CLOSED: ${formatOrgTimestamp(point.doneAt)}`);

  return [
    `** ${point.done ? 'DONE' : 'TODO'} ${point.text}`,
    planning.join(' '),
    ...kontraktDrawer(),
  ];
};

const buildNode = (contract, today) => {
  const keyword = isKept(contract) ? 'DONE' : 'TODO';
  const lines = [
    `* ${keyword} Tagesvertrag vom ${formatOrgDate(today)} :${CONTRACT_TAG}:`,
    ...kontraktDrawer(),
  ];

  (contract.points || []).forEach((point) => lines.push(...pointLines(point, today)));

  return `${lines.join('\n')}\n`;
};

/**
 * The file contents with the day's contract written as the contract-tagged
 * node - appended when the file has none yet, and the whole file is created
 * when there is no file at all (contents null).
 *
 * The node's entire subtree is replaced, not just a drawer: now that the
 * points are themselves headlines there is no narrower data-only region left
 * to patch in place. Nothing outside the subtree is touched.
 */
export const upsertContractNode = (contents, contract, today) => {
  const node = buildNode(contract, today);

  if (contents === null || contents === undefined || contents === '') {
    return node;
  }

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isContractHeadline(line));

  if (headlineIndex === -1) {
    const terminated = contents.endsWith('\n') ? contents : `${contents}\n`;
    return `${terminated}\n${node}`;
  }

  const end = endOfSubtreeIndex(lines, headlineIndex);
  const nodeLines = node.split('\n');
  nodeLines.pop(); // the trailing '' left by node's closing newline

  return [...lines.slice(0, headlineIndex), ...nodeLines, ...lines.slice(end)].join('\n');
};

/** The promise the declaring headline carries until the user rewrites it. */
export const DEFAULT_ZUSAGE = 'Jeden Tag, an dem ein Vertrag steht, ist er am Abend erfüllt';

const DECLARATION_REGEX = new RegExp(`^:KONTRAKT_ID:\\s*${CONTRACT_KONTRAKT_ID}\\s*$`);

/** Whether this file already declares the Tagesvertrag strand. */
export const hasStrandDeclaration = (contents) =>
  `${contents || ''}`.split('\n').some((line) => DECLARATION_REGEX.test(line.trim()));

/**
 * The file contents with the Tagesvertrag strand declared, or unchanged when it
 * already is. Declaring is a once-ever act: the Kennung is a promise to the
 * future, and a second declaration of the same one in another file would make
 * membership depend on which file a reader happened to scan first.
 *
 * The headline deliberately carries no TODO state - a declaring headline may be
 * a pure container (README-ORGMODE §2.1), and "kein Zustand, kein Knoten" keeps
 * a standing declaration out of the entropy scoring. No KONTRAKT_START either:
 * left out, the strand begins at its members' earliest date, which is the first
 * day that was actually signed rather than the day this line got written.
 */
export const upsertStrandDeclaration = (contents, zusage) => {
  if (hasStrandDeclaration(contents)) return contents;

  const promise = `${zusage || ''}`.trim() || DEFAULT_ZUSAGE;
  const node =
    `* Tagesvertrag\n` +
    `:PROPERTIES:\n` +
    `:${KONTRAKT_ID}: ${CONTRACT_KONTRAKT_ID}\n` +
    `:${KONTRAKT_MIT}: selbst\n` +
    `:${KONTRAKT_ZUSAGE}: ${promise}\n` +
    `:END:\n`;

  if (contents === null || contents === undefined || contents === '') return node;

  const terminated = contents.endsWith('\n') ? contents : `${contents}\n`;
  return `${terminated}\n${node}`;
};

const POINT_REGEX = /^\*\*\s+(TODO|DONE)\s+(.*)$/;
const CLOSED_REGEX = /CLOSED:\s*(\[[^\]]*\])/;

/**
 * The contract out of a daily note, in the shape lib/contract.js works on -
 * `{ negotiatedAt, renegotiations, points }` - or null when the file has no
 * contract-tagged node.
 *
 * negotiatedAt/renegotiations are never written to the file (see buildNode), so
 * they always come back null/0 here, and the renegotiation count is only
 * meaningful for the length of one open page. There is now a properties drawer
 * that could hold them - it carries the Kontrakt membership - but they stay out
 * of it deliberately: §2.6 declined a persisted renegotiation tally because a
 * record that counts how often you changed your mind nags, and having somewhere
 * to put it does not make that less true.
 */
export const extractContractFromFile = (contents) => {
  if (!contents) return null;

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isContractHeadline(line));
  if (headlineIndex === -1) return null;

  const end = endOfSubtreeIndex(lines, headlineIndex);
  const points = [];

  for (let index = headlineIndex + 1; index < end; index += 1) {
    const match = POINT_REGEX.exec(lines[index]);
    if (!match) continue;

    const done = match[1] === 'DONE';
    const text = match[2].trim();
    if (!text) continue;

    let doneAt = null;
    if (done && index + 1 < end) {
      const closedMatch = CLOSED_REGEX.exec(lines[index + 1]);
      if (closedMatch) doneAt = parseOrgTimestamp(closedMatch[1]);
    }

    points.push({ text, done, doneAt });
  }

  return normalizeContract({ negotiatedAt: null, renegotiations: 0, points });
};
